// REPLAY-2: pure word-alignment + active-word-search helpers for the karaoke player
// (TranscriptPlayer). Kept DOM-free and unit-testable on their own — see wordAlignment.test.ts.
import type { Segment, TranscriptWords, WordTiming } from '@scribe/core/types';

/** Per-segment word alignment: segmentId -> words, ONLY for segments whose joined word
 * text exactly reproduces segment.text (unedited). Edited/mismatched segments are simply
 * absent — the player renders those at segment level (REPLAY-2 fallback rule).
 *
 * Comparison: `seg.words.map((w) => w.text).join(' ') === segment.text` — exact, no
 * normalization (REPLAY-1's invariant; if it proves flaky against real AWS output,
 * surface it, don't loosen silently). */
export function alignWordsToSegments(
  words: TranscriptWords | undefined,
  segments: Segment[],
): Map<string, WordTiming[]> {
  const map = new Map<string, WordTiming[]>();
  if (!words?.words_available || !words.segments) return map;

  const segmentById = new Map(segments.map((s) => [s.id, s]));
  for (const wordSeg of words.segments) {
    const segment = segmentById.get(wordSeg.segment_id);
    if (!segment) continue; // segment id in the words payload not present in segments — ignore
    const joined = wordSeg.words.map((w) => w.text).join(' ');
    if (joined === segment.text) {
      map.set(wordSeg.segment_id, wordSeg.words);
    }
  }
  return map;
}

/** Index of the word containing currentMs (start_ms <= t < end_ms), else the latest word
 * already started (covers inter-word gaps), else -1 before the first word. Binary search
 * over start_ms — called per timeupdate for the active segment only. */
export function findActiveWordIndex(words: WordTiming[], currentMs: number): number {
  if (words.length === 0 || currentMs < words[0]!.start_ms) return -1;

  // Binary search for the last word whose start_ms <= currentMs.
  let lo = 0;
  let hi = words.length - 1;
  while (lo < hi) {
    const mid = Math.ceil((lo + hi) / 2);
    if (words[mid]!.start_ms <= currentMs) {
      lo = mid;
    } else {
      hi = mid - 1;
    }
  }
  return lo;
}
